import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import type { LoadPlanningControllerGetEarliestConsumptionPerLocationData } from '@/src/api/types/procurement'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import type { ProcurementEnergyType } from '@/src/features/procurement-and-markets/load-planning/LoadPlanning.types'
import { normalizeDataPointTimestamp } from '@/src/features/procurement-and-markets/utils/procurementTimezone.utils'
import { ONE_MINUTE_IN_MS } from '@/src/utils/constants'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'

export interface LoadPlanningStrategyMetadata {
  energyType?: ProcurementEnergyType
  year?: number
}

const mapStrategyToMetadata = (strategy: { energyType?: unknown; year?: unknown }): LoadPlanningStrategyMetadata => ({
  energyType: strategy.energyType as ProcurementEnergyType | undefined,
  year: strategy.year ? Number(strategy.year) : undefined,
})

export const useLoadedStrategyMetadata = () => {
  const { setId: strategyId } = useParams<{ setId: string }>()
  const [searchParams] = useSearchParams()
  const strategyYear = searchParams.get('year')
  const strategyEnergyType = searchParams.get('energyType')

  return useQuery({
    queryKey: queryKeys.procurement.strategies.fetchOne.toKeyWithArgs(strategyId, strategyYear, strategyEnergyType),
    queryFn: async () => {
      if (strategyYear && strategyEnergyType) {
        return {
          energyType: strategyEnergyType as ProcurementEnergyType,
          year: Number(strategyYear),
        } satisfies LoadPlanningStrategyMetadata
      }

      const strategy = await API.procurements.fetchHedgedStrategy({ strategyId: strategyId! })
      return mapStrategyToMetadata(strategy)
    },
    enabled: (!!strategyYear && !!strategyEnergyType) || !!strategyId,
  })
}

export interface UseLoadPlanningCurveProps {
  companyId?: number
  locationId?: number
  energyType: ProcurementEnergyType
  year?: number
  granularity?: GranularityEnum
}

export const useLoadPlanningCurve = (data: UseLoadPlanningCurveProps) =>
  useQuery({
    queryKey: queryKeys.procurement.loadPlanning.fetchAllForCompany.toKeyWithArgs(
      data.energyType,
      data.year,
      data.companyId,
      data.locationId,
      data.granularity
    ),
    queryFn: async () => {
      const payload = await API.procurements.fetchLoadForecast({
        year: String(data.year!),
        energyType: data.energyType,
        locationId: data.locationId,
        companyId: data.companyId!,
        include15MinResults: data.granularity === GranularityEnum.FIFTEEN_MINUTES,
        includeHourlyResults: data.granularity === GranularityEnum.ONE_HOUR,
      })

      return {
        ...payload,
        hourlyData: payload.hourlyData?.map(normalizeDataPointTimestamp),
        monthlyData: payload.monthlyData?.map(normalizeDataPointTimestamp),
      }
    },
    enabled: !!data.year && !!data.companyId,
    placeholderData: keepPreviousData,
    staleTime: ONE_MINUTE_IN_MS * 10,
  })

export interface UseLoadPlanningAdjustmentsProps {
  companyId?: number
  locationId?: number
  energyType: ProcurementEnergyType
  year?: number
}

export const useLoadPlanningAdjustments = (data: UseLoadPlanningAdjustmentsProps) =>
  useQuery({
    queryKey: queryKeys.procurement.loadAdjustmentsV2.fetchAll.toKeyWithArgs(data.energyType, data.year, data.companyId, data.locationId),
    queryFn: () =>
      API.procurements.fetchAllAdjustments({
        year: data.year!.toString(),
        energyType: data.energyType,
        locationId: data.locationId,
        companyId: data.companyId!,
      }),
    enabled: !!data.year && !!data.companyId && !!data.energyType,
  })

export const useLoadPlanningEarliestConsumption = (
  data: LoadPlanningControllerGetEarliestConsumptionPerLocationData & { disabled?: boolean }
) =>
  useQuery({
    queryKey: queryKeys.procurement.loadPlanning.fetchEarliestConsumption.toKeyWithArgs(data.companyId, data.energyType, data.year),
    queryFn: () =>
      API.procurements.fetchEarliestConsumptionPerLocation({
        energyType: data.energyType,
        companyId: data.companyId,
        year: data.year,
      }),
    // year is actually optional, so its safe to requsest without year.
    enabled: !data.disabled && !!data.companyId && !!data.energyType,
  })
